/**
 * The installed icon set. **This is the only file that names one.**
 *
 * Today it is Font Awesome Pro 7, Classic Regular, imported one icon at a
 * time so the build keeps only what is used. Nothing else in `apps/web`
 * imports `@fortawesome/*`, and nothing else knows the set exists.
 *
 * ## Changing the set
 *
 * Rewrite this file and nothing else:
 *
 * 1. Install the replacement.
 * 2. Map every `IconName` to an `IconSource` — a width, a height, and the
 *    artwork's own path data, unmodified.
 * 3. Delete the old dependency and the `@fortawesome` lines in `/.npmrc`.
 *
 * `ICON_PACK` is typed `Record<IconName, IconSource>`, so leaving a name out
 * fails the build rather than rendering an empty box at a customer.
 *
 * ## Why the artwork is read as data
 *
 * A Font Awesome icon definition carries `icon: [width, height, ligatures,
 * unicode, path]`. Reading those five fields is what the set's own React
 * component does. Taking the data directly keeps `@fortawesome/react-
 * fontawesome` and `@fortawesome/fontawesome-svg-core` out of the bundle —
 * about 14 KiB gzipped for an interface that draws a handful of decorative
 * marks — and keeps the seam free of any type belonging to one set.
 *
 * The path data is used exactly as published. It is never edited, recombined
 * into a new character, or redrawn.
 *
 * ## Licence
 *
 * Font Awesome Pro is licensed to the owner, who confirmed commercial use on
 * 2026-09-20. The package token is never committed: `/.npmrc` holds a
 * `${FONTAWESOME_NPM_AUTH_TOKEN}` reference that npm expands at install time.
 */

import { faBinoculars } from "@fortawesome/pro-regular-svg-icons/faBinoculars";
import { faBuilding } from "@fortawesome/pro-regular-svg-icons/faBuilding";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { faEllipsis } from "@fortawesome/pro-regular-svg-icons/faEllipsis";
import { faGaugeHigh } from "@fortawesome/pro-regular-svg-icons/faGaugeHigh";
import { faHandshake } from "@fortawesome/pro-regular-svg-icons/faHandshake";
import { faHouse } from "@fortawesome/pro-regular-svg-icons/faHouse";
import { faShieldHalved } from "@fortawesome/pro-regular-svg-icons/faShieldHalved";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { faBoxOpen } from "@fortawesome/pro-regular-svg-icons/faBoxOpen";
import { faBroom } from "@fortawesome/pro-regular-svg-icons/faBroom";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faClipboardList } from "@fortawesome/pro-regular-svg-icons/faClipboardList";
import { faClipboardListCheck } from "@fortawesome/pro-regular-svg-icons/faClipboardListCheck";
import { faComet } from "@fortawesome/pro-regular-svg-icons/faComet";
import { faDuck } from "@fortawesome/pro-regular-svg-icons/faDuck";
import { faFlag } from "@fortawesome/pro-regular-svg-icons/faFlag";
import { faFolderOpen } from "@fortawesome/pro-regular-svg-icons/faFolderOpen";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons/faMagnifyingGlass";
import { faMap } from "@fortawesome/pro-regular-svg-icons/faMap";
import { faMeteor } from "@fortawesome/pro-regular-svg-icons/faMeteor";
import { faEarthAmericas } from "@fortawesome/pro-regular-svg-icons/faEarthAmericas";
import { faMoon } from "@fortawesome/pro-regular-svg-icons/faMoon";
import { faParachuteBox } from "@fortawesome/pro-regular-svg-icons/faParachuteBox";
import { faPencil } from "@fortawesome/pro-regular-svg-icons/faPencil";
import { faPlanetRinged } from "@fortawesome/pro-regular-svg-icons/faPlanetRinged";
import { faRobot } from "@fortawesome/pro-regular-svg-icons/faRobot";
import { faRocket } from "@fortawesome/pro-regular-svg-icons/faRocket";
import { faSatellite } from "@fortawesome/pro-regular-svg-icons/faSatellite";
import { faScrewdriverWrench } from "@fortawesome/pro-regular-svg-icons/faScrewdriverWrench";
import { faShuttleSpace } from "@fortawesome/pro-regular-svg-icons/faShuttleSpace";
import { faSignHanging } from "@fortawesome/pro-regular-svg-icons/faSignHanging";
import { faStar } from "@fortawesome/pro-regular-svg-icons/faStar";
import { faSun } from "@fortawesome/pro-regular-svg-icons/faSun";
import { faTelescope } from "@fortawesome/pro-regular-svg-icons/faTelescope";
import { faUfo } from "@fortawesome/pro-regular-svg-icons/faUfo";
import { faUserAstronaut } from "@fortawesome/pro-regular-svg-icons/faUserAstronaut";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faArrowDownToLine } from "@fortawesome/pro-regular-svg-icons/faArrowDownToLine";
import { faArrowUpFromLine } from "@fortawesome/pro-regular-svg-icons/faArrowUpFromLine";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons/faTrashCan";
import { faPen } from "@fortawesome/pro-regular-svg-icons/faPen";
import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons/faPaperPlane";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faFilter } from "@fortawesome/pro-regular-svg-icons/faFilter";
import { faArrowsRotate } from "@fortawesome/pro-regular-svg-icons/faArrowsRotate";
import { faCopy } from "@fortawesome/pro-regular-svg-icons/faCopy";
import { faPrint } from "@fortawesome/pro-regular-svg-icons/faPrint";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare";
import { faFileImport } from "@fortawesome/pro-regular-svg-icons/faFileImport";
import { faArrowRotateLeft } from "@fortawesome/pro-regular-svg-icons/faArrowRotateLeft";
import { faBoxArchive } from "@fortawesome/pro-regular-svg-icons/faBoxArchive";
import { faListCheck } from "@fortawesome/pro-regular-svg-icons/faListCheck";
import { faStamp } from "@fortawesome/pro-regular-svg-icons/faStamp";
import { faMessages } from "@fortawesome/pro-regular-svg-icons/faMessages";
import { faChartPie } from "@fortawesome/pro-regular-svg-icons/faChartPie";
import { faChartLine } from "@fortawesome/pro-regular-svg-icons/faChartLine";
import { faSignature } from "@fortawesome/pro-regular-svg-icons/faSignature";
import { faClipboardCheck } from "@fortawesome/pro-regular-svg-icons/faClipboardCheck";
import { faPaperclip } from "@fortawesome/pro-regular-svg-icons/faPaperclip";
import { faRoute } from "@fortawesome/pro-regular-svg-icons/faRoute";
import { faUserCheck } from "@fortawesome/pro-regular-svg-icons/faUserCheck";
import { faFileChartColumn } from "@fortawesome/pro-regular-svg-icons/faFileChartColumn";
import { faBriefcaseMedical } from "@fortawesome/pro-regular-svg-icons/faBriefcaseMedical";
import { faBug } from "@fortawesome/pro-regular-svg-icons/faBug";
import { faHeartPulse } from "@fortawesome/pro-regular-svg-icons/faHeartPulse";
import { faDatabase } from "@fortawesome/pro-regular-svg-icons/faDatabase";
import { faChessKnight } from "@fortawesome/pro-regular-svg-icons/faChessKnight";
import { faCodeCompare } from "@fortawesome/pro-regular-svg-icons/faCodeCompare";
import { faFileLines } from "@fortawesome/pro-regular-svg-icons/faFileLines";
import { faBoxesStacked } from "@fortawesome/pro-regular-svg-icons/faBoxesStacked";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { faGlobe } from "@fortawesome/pro-regular-svg-icons/faGlobe";
import { faUsers } from "@fortawesome/pro-regular-svg-icons/faUsers";
import { faBuildingUser } from "@fortawesome/pro-regular-svg-icons/faBuildingUser";
import { faIdBadge } from "@fortawesome/pro-regular-svg-icons/faIdBadge";
import { faKey } from "@fortawesome/pro-regular-svg-icons/faKey";
import { faIdCard } from "@fortawesome/pro-regular-svg-icons/faIdCard";
import { faBullseye } from "@fortawesome/pro-regular-svg-icons/faBullseye";
import { faPlug } from "@fortawesome/pro-regular-svg-icons/faPlug";
import { faGear } from "@fortawesome/pro-regular-svg-icons/faGear";
import { faHeadset } from "@fortawesome/pro-regular-svg-icons/faHeadset";
import { faServer } from "@fortawesome/pro-regular-svg-icons/faServer";

import type { IconName, IconSource } from "./icon-names.ts";

/** The five fields a Font Awesome definition carries. */
interface FontAwesomeDefinition {
  readonly icon: readonly [number, number, unknown, unknown, string | readonly string[]];
}

/** Read one definition into the set-neutral shape. */
function source(definition: FontAwesomeDefinition): IconSource {
  const [width, height, , , path] = definition.icon;
  return {
    width,
    height,
    // A duotone style publishes two layers. Classic Regular publishes one.
    path: Array.isArray(path) ? ([path[0], path[1]] as [string, string]) : (path as string),
  };
}

export const ICON_PACK: Record<IconName, IconSource> = {
  // The space motifs.
  rocket: source(faRocket),
  astronaut: source(faUserAstronaut),
  moon: source(faMoon),
  earth: source(faEarthAmericas),
  planet: source(faPlanetRinged),
  satellite: source(faSatellite),
  telescope: source(faTelescope),
  star: source(faStar),
  flag: source(faFlag),
  meteor: source(faMeteor),
  comet: source(faComet),
  sun: source(faSun),
  ufo: source(faUfo),
  shuttle: source(faShuttleSpace),
  robot: source(faRobot),
  duck: source(faDuck),
  // The plain objects a scene puts beside them.
  map: source(faMap),
  checklist: source(faClipboardListCheck),
  folder: source(faFolderOpen),
  crate: source(faBoxOpen),
  tools: source(faScrewdriverWrench),
  magnifier: source(faMagnifyingGlass),
  check: source(faCircleCheck),
  clipboard: source(faClipboardList),
  pencil: source(faPencil),
  broom: source(faBroom),
  parachute: source(faParachuteBox),
  sign: source(faSignHanging),
  binoculars: source(faBinoculars),
  // The icons the interface needs to work: navigation, a row that opens, a
  // menu, a sheet that closes.
  house: source(faHouse),
  ellipsis: source(faEllipsis),
  "chevron-right": source(faChevronRight),
  "chevron-down": source(faChevronDown),
  close: source(faXmark),
  shield: source(faShieldHalved),
  building: source(faBuilding),
  handshake: source(faHandshake),
  gauge: source(faGaugeHigh),
  // The verbs of actions.
  "plus": source(faPlus),
  "download": source(faArrowDownToLine),
  "upload": source(faArrowUpFromLine),
  "trash": source(faTrashCan),
  "edit": source(faPen),
  "send": source(faPaperPlane),
  "approve": source(faCheck),
  "filter": source(faFilter),
  "refresh": source(faArrowsRotate),
  "copy": source(faCopy),
  "print": source(faPrint),
  "external": source(faArrowUpRightFromSquare),
  "import": source(faFileImport),
  "undo": source(faArrowRotateLeft),
  "archive": source(faBoxArchive),
  // The sections of the navigation.
  "my-work": source(faListCheck),
  "approvals": source(faStamp),
  "asks": source(faMessages),
  "summary": source(faChartPie),
  "dashboard": source(faChartLine),
  "statements": source(faSignature),
  "assessments": source(faClipboardCheck),
  "evidence": source(faPaperclip),
  "plan": source(faRoute),
  "third-party": source(faUserCheck),
  "reports": source(faFileChartColumn),
  "hipaa": source(faBriefcaseMedical),
  "vulnerabilities": source(faBug),
  "monitoring": source(faHeartPulse),
  "backups": source(faDatabase),
  "tabletop": source(faChessKnight),
  "changes": source(faCodeCompare),
  "documents": source(faFileLines),
  "inventories": source(faBoxesStacked),
  "risks": source(faTriangleExclamation),
  "globe": source(faGlobe),
  "people": source(faUsers),
  "providers": source(faBuildingUser),
  "roles": source(faIdBadge),
  "key": source(faKey),
  "employees": source(faIdCard),
  "scope": source(faBullseye),
  "connections": source(faPlug),
  "settings": source(faGear),
  "helpdesk": source(faHeadset),
  "server": source(faServer),
};

/** The set in use, for the about panel and the coverage record. */
export const ICON_PACK_NAME = "Font Awesome Pro 7 — Classic Regular";
